// =============================================================================
// BrightPath - Health Log React Query Hooks
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { healthApi } from '@/features/health/api/healthApi';
import type {
  HealthLog,
  HealthLogParams,
  CreateHealthLogDTO,
  UpdateHealthLogDTO,
  PaginatedResponse,
} from '@/features/health/types/healthTypes';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const healthLogKeys = {
  all: ['healthLogs'] as const,
  lists: () => [...healthLogKeys.all, 'list'] as const,
  list: (childId: string, params?: HealthLogParams) =>
    [...healthLogKeys.lists(), childId, params] as const,
  infinite: (childId: string, params?: Omit<HealthLogParams, 'page'>) =>
    [...healthLogKeys.all, 'infinite', childId, params] as const,
  recent: (childId: string, limit?: number) =>
    [...healthLogKeys.all, 'recent', childId, limit] as const,
  details: () => [...healthLogKeys.all, 'detail'] as const,
  detail: (logId: string) => [...healthLogKeys.details(), logId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetch paginated health logs for a child
 */
export function useHealthLogs(
  childId: string,
  params?: HealthLogParams,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<HealthLog>, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: healthLogKeys.list(childId, params),
    queryFn: () => healthApi.getHealthLogs(childId, params),
    enabled: !!childId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    ...options,
  });
}

/**
 * Fetch health logs with infinite scrolling
 */
export function useInfiniteHealthLogs(
  childId: string,
  params?: Omit<HealthLogParams, 'page'>,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: healthLogKeys.infinite(childId, params),
    queryFn: ({ pageParam = 1 }) =>
      healthApi.getHealthLogs(childId, { ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.hasPrevious ? firstPage.page - 1 : undefined,
    enabled: !!childId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch recent health logs (last N entries)
 */
export function useRecentHealthLogs(
  childId: string,
  limit: number = 5,
  options?: Omit<UseQueryOptions<HealthLog[], Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: healthLogKeys.recent(childId, limit),
    queryFn: () => healthApi.getRecentHealthLogs(childId, limit),
    enabled: !!childId,
    staleTime: 1000 * 60, // 1 minute
    ...options,
  });
}

/**
 * Fetch a single health log by ID
 */
export function useHealthLog(
  logId: string | undefined,
  options?: Omit<UseQueryOptions<HealthLog, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: healthLogKeys.detail(logId!),
    queryFn: () => healthApi.getHealthLog(logId!),
    enabled: !!logId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Create a new health log
 */
export function useCreateHealthLog(
  childId: string,
  options?: UseMutationOptions<HealthLog, Error, CreateHealthLogDTO>
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateHealthLogDTO) =>
      healthApi.createHealthLog(childId, data),
    onSuccess: (newLog) => {
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.lists(),
      });
      
      // Invalidate recent logs
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.recent(childId),
      });
      
      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: ['healthLogStats', childId],
      });
      
      toast({
        title: 'Log created',
        description: `${newLog.logType.name} log has been saved.`,
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error creating log',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

/**
 * Update an existing health log
 */
export function useUpdateHealthLog(
  options?: UseMutationOptions<
    HealthLog,
    Error,
    { logId: string; data: UpdateHealthLogDTO }
  >
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ logId, data }) => healthApi.updateHealthLog(logId, data),
    onSuccess: (updatedLog) => {
      // Update the specific log in cache
      queryClient.setQueryData(
        healthLogKeys.detail(updatedLog.id),
        updatedLog
      );
      
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.lists(),
      });
      
      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: ['healthLogStats', updatedLog.childId],
      });
      
      toast({
        title: 'Log updated',
        description: 'Your changes have been saved.',
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating log',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

/**
 * Delete a health log
 */
export function useDeleteHealthLog(
  options?: UseMutationOptions<void, Error, { logId: string; childId: string }>
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ logId }) => healthApi.deleteHealthLog(logId),
    onSuccess: (_, { logId, childId }) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: healthLogKeys.detail(logId),
      });
      
      // Invalidate lists
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.lists(),
      });
      
      // Invalidate recent logs
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.recent(childId),
      });
      
      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: ['healthLogStats', childId],
      });
      
      toast({
        title: 'Log deleted',
        description: 'The health log has been removed.',
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting log',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

/**
 * Bulk delete health logs
 */
export function useBulkDeleteHealthLogs(
  childId: string,
  options?: UseMutationOptions<{ deleted: number }, Error, string[]>
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (logIds: string[]) => healthApi.bulkDeleteHealthLogs(logIds),
    onSuccess: ({ deleted }) => {
      // Invalidate all health log queries
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.all,
      });
      
      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: ['healthLogStats', childId],
      });
      
      toast({
        title: 'Logs deleted',
        description: `${deleted} health log(s) have been removed.`,
        variant: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting logs',
        description: error.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    },
    ...options,
  });
}

// =============================================================================
// Optimistic Updates Helper
// =============================================================================

/**
 * Create health log with optimistic update
 */
export function useCreateHealthLogOptimistic(childId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateHealthLogDTO) =>
      healthApi.createHealthLog(childId, data),
    onMutate: async (newLogData) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: healthLogKeys.lists(),
      });

      // Snapshot previous value
      const previousLogs = queryClient.getQueryData(
        healthLogKeys.recent(childId)
      );

      // Optimistically add to recent logs
      // Note: This is a simplified optimistic update
      // In production, you'd want to properly format the optimistic log

      return { previousLogs };
    },
    onError: (err, _, context) => {
      // Rollback on error
      if (context?.previousLogs) {
        queryClient.setQueryData(
          healthLogKeys.recent(childId),
          context.previousLogs
        );
      }
      toast({
        title: 'Error',
        description: 'Failed to save health log. Please try again.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // Refetch after error or success
      queryClient.invalidateQueries({
        queryKey: healthLogKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: ['healthLogStats', childId],
      });
    },
    onSuccess: (newLog) => {
      toast({
        title: 'Saved',
        description: `${newLog.logType.name} log recorded.`,
        variant: 'success',
      });
    },
  });
}

// =============================================================================
// Prefetch Helper
// =============================================================================

/**
 * Prefetch health logs for a child (useful for navigation)
 */
export function usePrefetchHealthLogs(childId: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.prefetchQuery({
      queryKey: healthLogKeys.list(childId, { page: 1, pageSize: 20 }),
      queryFn: () =>
        healthApi.getHealthLogs(childId, { page: 1, pageSize: 20 }),
      staleTime: 1000 * 60 * 2,
    });
  };
}


